import cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import {
  getImpersonatingUserUuid,
  getLatestCognitoToken,
  isCognitoAuthentication,
  isImpersonationSession,
} from '../helpers/auth/cognito';
import { AUTH_TOKEN, USER_ID } from '../constants-togather/auth';

const gatValidSessionToken = async (token, forceRefresh) => {
  let sessionToken = token;
  if (!token) return sessionToken;

  if (token && isCognitoAuthentication(token) && !isImpersonationSession()) {
    sessionToken = await getLatestCognitoToken(forceRefresh);
  }

  try {
    const decodedToken = jwtDecode(sessionToken);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime ? null : sessionToken;
  } catch {
    return null;
  }
};

export async function getToken(request = { token: null }) {
  const cookieToken = cookies.get(AUTH_TOKEN) || request.token;

  // Legacy / impersonation (or an explicitly-passed token): the cookie token is
  // the bearer.
  if (cookieToken) {
    const sessionToken = await gatValidSessionToken(
      cookieToken,
      request.forceRefresh
    );

    if (sessionToken) {
      return request.token ?? sessionToken;
    }
  }

  // No valid cookie token: fall back to the shared Cognito session. This covers
  // cross-FE redirects before the cookie is restored.
  //
  // IMPORTANT: never sign out or redirect here. The previous implementation
  // called cognitoSignOut() + redirected on a missing token, which destroyed
  // valid sessions on a transient race (async session restore / SSO token
  // exchange) and caused flaky logouts. Genuinely unauthenticated users are
  // handled by route guards; explicit logout goes through logoutUser.
  const idToken = await getLatestCognitoToken(request.forceRefresh);

  return idToken ?? null;
}

export async function withAuthHeader(request = {}) {
  const token = await getToken(request);

  if (!token) {
    // No bearer available yet (e.g. session still restoring): send the request
    // as-is rather than a malformed "Bearer null".
    return request;
  }

  if (isCognitoAuthentication(token)) {
    return {
      ...request,
      headers: {
        ...(request.headers || {}),
        // Send the id token for Cognito requests (it carries `custom:databaseId`,
        // so the backend derives the user id — no `x-user-id` for normal
        // requests). During impersonation we send the admin-selected target
        // (honoured by the backend only for admins), mirroring apollo.
        authorization: `Bearer ${await getLatestCognitoToken()}`,
        'x-new-auth': true,
        ...(isImpersonationSession() && {
          'x-user-id': cookies.get(USER_ID),
          'x-impersonating-user-uuid': getImpersonatingUserUuid(),
        }),
      },
    };
  }

  const authorization = `Bearer ${token}`;
  const headers = { ...(request.headers || {}), authorization };

  return { ...request, headers };
}

export const createAuthorization = () => async (request, next) =>
  await next(await withAuthHeader(request));
